import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@easydent/shared-ui';
import { useChatStore } from '@/stores/chatStore';
import { useVoiceInput } from '@/hooks/useVoiceInput';

const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
const dayLabel = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });

/**
 * One conversation: the messages and the composer.
 *
 * <p>
 * Extracted from the messages panel so the support page can render a ticket in place rather than
 * bouncing the reader into a floating widget. Extracted rather than copied, deliberately — two
 * copies of a chat transcript drift, and the one nobody is looking at is the one that rots.
 * </p>
 *
 * <p>
 * Everything comes from `chatStore`, which holds a single active room and receives realtime pushes
 * regardless of which surface is mounted. The caller opens the room; this only renders it.
 * </p>
 */
const ChatThread: React.FC<{
  /** Rendered above the transcript — the panel supplies a back arrow, the page a title. */
  header?: React.ReactNode;
  /** Show who said what. Off for direct messages, where there are only two people. */
  showSender?: boolean;
  className?: string;
}> = ({ header, showSender = true, className = '' }) => {
  const { t } = useTranslation('common');
  const messages = useChatStore((s) => s.messages);
  const loadingMessages = useChatStore((s) => s.loadingMessages);
  const sending = useChatStore((s) => s.sending);
  const send = useChatStore((s) => s.send);

  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Speaking fills the box rather than sending: a mis-heard word should be caught before a
  // colleague — or easyDent support — reads it.
  const { supported: micSupported, recording: listening, toggle: toggleMic } = useVoiceInput({
    onResult: (transcript) => setText((cur) => (cur ? `${cur} ${transcript}` : transcript)),
  });

  useEffect(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, [messages]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    await send(text);
    setText('');
  };

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`}>
      {header}

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto bg-canvas px-3 py-3">
        {loadingMessages ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-subtle-ink">{t('chat.noMessages', 'No messages yet')}</p>
        ) : messages.map((m, i) => {
          const showDay = i === 0 || dayLabel(m.createdAt) !== dayLabel(messages[i - 1].createdAt);
          return (
            <div key={m.id}>
              {showDay && <div className="my-2 text-center text-[11px] text-subtle-ink">{dayLabel(m.createdAt)}</div>}
              <div className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.mine ? 'bg-brand-600 text-white' : 'bg-surface text-body-strong shadow-sm'}`}>
                  {!m.mine && showSender && <div className="mb-0.5 text-[11px] font-medium text-brand-ink-strong">{m.senderName}</div>}
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div className={`mt-0.5 text-right text-[10px] ${m.mine ? 'text-brand-100' : 'text-subtle-ink'}`}>{time(m.createdAt)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={submit} className="flex items-center gap-2 border-t border-line-soft p-3">
        {micSupported && (
          <button
            type="button"
            onClick={toggleMic}
            aria-label={t('chat.speak', 'Speak a message')}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${listening ? 'border-red-300 bg-red-50 text-red-600' : 'border-line-strong text-muted hover:bg-canvas'}`}
          >
            {listening ? (
              <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-red-500" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4" />
              </svg>
            )}
          </button>
        )}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={listening ? t('assistant.listening', 'Listening…') : t('chat.placeholder', 'Write a message…')}
          className="min-w-0 flex-1 rounded-full border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />
        <Button type="submit" size="sm" loading={sending} disabled={!text.trim()}>{t('chat.send', 'Send')}</Button>
      </form>
    </div>
  );
};

export default ChatThread;
